// Service pour gérer les appels API OAuth v2 hybride et connexion de repositories
import axios, { AxiosInstance } from 'axios';
import { csrfTokenManager } from '../utils/csrfTokenManager';
import type { OAuthProvider } from '../types/api';

// Utilise window._env_ (runtime) avec fallback sur process.env (build time)
const API_BASE_URL =
  (window as any)._env_?.REACT_APP_API_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:8000';

// ==================== Types pour le nouveau système OAuth v2 ====================

export interface RepositorySearchResult {
  id: string | number;
  name: string;
  owner: string;
  full_name: string;
  url: string;
  clone_url: string;
  ssh_url?: string;
  private: boolean;
  default_branch: string;
  description?: string;
  language?: string;
  topics?: string[];
  stars?: number;
  forks?: number;
}

export interface RepositorySearchResponse {
  provider: string;
  query?: string;
  repositories: RepositorySearchResult[];
  count: number;
  limit: number;
  offset: number;
  message?: string;
}

export interface Repository {
  id: number;
  name: string;
  owner: string;
  full_name: string;
  url: string;
  clone_url: string;
  default_branch: string;
  private: boolean;
  description?: string;
}

export interface UserInfo {
  id: number;
  login: string;
  email?: string;
  avatar_url?: string;
  name?: string;
}

export interface InitiateOAuthResponse {
  auth_url: string;
  state: string;
  provider: string;
}

export interface RepositoriesResponse {
  state: string;
  provider: string;
  user_info: UserInfo;
  repositories: Repository[];
  count: number;
}

export interface ConnectedRepository {
  id: number;
  provider: string;
  repository_name: string;
  repository_owner: string;
  repository_url: string;
  clone_url: string;
  default_branch: string;
  monitored_branch: string;
  is_active: boolean;
  webhook_enabled: boolean;
  webhook_id?: string;
  last_sync_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ListConnectedReposResponse {
  total: number;
  repositories: ConnectedRepository[];
}

class RepositoryService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send httpOnly cookies automatically
    });

    // Add CSRF headers for state-changing methods (auth via httpOnly cookie)
    this.client.interceptors.request.use((config) => {
      const method = config.method?.toUpperCase();
      if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        const csrfHeaders = csrfTokenManager.getHeaders();
        Object.assign(config.headers, csrfHeaders);
      }
      return config;
    });
  }

  /**
   * Initie un flow OAuth
   */
  async initiateOAuth(
    provider: 'github' | 'gitlab',
    monitoredBranch: string = 'main'
  ): Promise<InitiateOAuthResponse> {
    try {
      const response = await this.client.post<{ authorization_url: string; state: string }>(
        `/api/v1/git/authorize`,
        {
          provider,
          monitored_branch: monitoredBranch,
        }
      );
      return {
        auth_url: response.data.authorization_url,
        state: response.data.state,
        provider,
      };
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to initiate OAuth'
      );
    }
  }

  /**
   * Récupère les repositories disponibles après OAuth
   */
  async getAvailableRepositories(state: string): Promise<RepositoriesResponse> {
    try {
      const response = await this.client.get<{ repositories: Repository[]; user_info: UserInfo; provider: string }>(
        `/api/v1/git/session/${state}`
      );
      return {
        state,
        provider: response.data.provider,
        user_info: response.data.user_info,
        repositories: response.data.repositories,
        count: response.data.repositories.length,
      };
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail ||
          'Failed to fetch repositories'
      );
    }
  }

  /**
   * Connecte un repository sélectionné
   */
  async selectRepository(
    state: string,
    repoId: number,
    provider: string = 'github'
  ): Promise<any> {
    try {
      const response = await this.client.post<any>(
        `/api/v1/git/repositories/${repoId}/connect`,
        {
          provider,
          provider_repo_id: String(repoId),
        }
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail ||
          'Failed to connect repository'
      );
    }
  }

  /**
   * Récupère les repositories connectés de l'utilisateur
   */
  async getMyRepositories(): Promise<ListConnectedReposResponse> {
    try {
      const response = await this.client.get<{ repositories: ConnectedRepository[] }>(
        '/api/v1/git/repositories/connected'
      );
      return {
        total: response.data.repositories.length,
        repositories: response.data.repositories,
      };
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail ||
          'Failed to fetch my repositories'
      );
    }
  }

  /**
   * Vérifie le statut du token d'un repository
   */
  async checkTokenStatus(repoId: number): Promise<any> {
    try {
      const response = await this.client.post(
        `/api/v1/git/repositories/${repoId}/test-token`
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail ||
          'Failed to check token status'
      );
    }
  }

  /**
   * Déconnecte un repository
   */
  async disconnectRepository(repoId: number): Promise<any> {
    try {
      const response = await this.client.delete(
        `/api/v1/git/repositories/${repoId}`
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail ||
          'Failed to disconnect repository'
      );
    }
  }

  // ==================== OAUTH V2 HYBRIDE ====================

  /**
   * Les fournisseurs avec lesquels une connexion de depot peut etre lancee.
   *
   * LE CONNECTEUR CODAIT `github` ET `gitlab` EN DUR : il ne pouvait donc ni savoir
   * qu'une GitHub App est disponible, ni cesser de proposer un fournisseur
   * desactive. Deviner une configuration, c'est afficher un bouton qui mene a un
   * 404.
   */
  async listOAuthProviders(): Promise<OAuthProvider[]> {
    const response = await this.client.get('/api/v1/oauth-v2/providers');
    return response.data;
  }

  /**
   * Initie un flow OAuth v2 hybride
   * Retourne un state token et une URL d'autorisation
   * Session stockée côté serveur (~1KB, pas 40KB)
   */
  async initiateOAuthV2(
    // Le NOM du fournisseur tel que l'API le declare -- `github`, `gitlab`,
    // `github_app`. Une union figee ici aurait interdit l'App sans rien dire.
    provider: string
  ): Promise<{ auth_url: string; state: string; provider: string }> {
    try {
      const response = await this.client.get(
        `/api/v1/oauth-v2/authorize/${provider}`
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || `Failed to initiate OAuth for ${provider}`
      );
    }
  }

  /**
   * Recherche les repositories pour une session OAuth donnée
   * Approche on-demand: pas de pre-chargement de tous les repos
   * Pas de limite arbitraire de 100 repos
   */
  async searchRepositories(
    state: string,
    query?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<RepositorySearchResponse> {
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const response = await this.client.get(
        `/api/v1/oauth-v2/session/${state}/search?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to search repositories'
      );
    }
  }

  /**
   * Liste les repositories avec pagination (pas de recherche)
   */
  async listRepositories(
    state: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<RepositorySearchResponse> {
    try {
      const params = new URLSearchParams();
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const response = await this.client.get(
        `/api/v1/oauth-v2/session/${state}/repositories?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to list repositories'
      );
    }
  }

  /**
   * Connecte un repository sélectionné
   * Webhook automatiquement installé côté serveur
   */
  async connectRepository(
    state: string,
    repo_owner: string,
    repo_name: string,
    projectId?: string,
    monitored_branch: string = 'main'
  ): Promise<any> {
    try {
      const response = await this.client.post(
        '/api/v1/oauth-v2/connect',
        {
          state,
          repo_owner,
          repo_name,
          project_id: projectId,
          monitored_branch
        }
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to connect repository'
      );
    }
  }

  /**
   * Récupère les repositories connectés de l'utilisateur
   */
  async getConnectedRepositoriesV2(): Promise<any[]> {
    try {
      const response = await this.client.get(
        '/api/v1/oauth-v2/repositories'
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to fetch connected repositories'
      );
    }
  }

  /**
   * Déconnecte un repository et supprime le webhook
   */
  async disconnectRepositoryV2(repoId: number | string): Promise<any> {
    try {
      const response = await this.client.delete(
        `/api/v1/oauth-v2/repositories/${repoId}`
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to disconnect repository'
      );
    }
  }

  /**
   * Vérifie la santé du webhook d'un repository
   */
  async checkWebhookHealth(repoId: number | string): Promise<any> {
    try {
      const response = await this.client.get(
        `/api/v1/oauth-v2/repositories/${repoId}/webhook-health`
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to check webhook health'
      );
    }
  }

  /**
   * Récupère les statistiques des sessions OAuth
   */
  async getOAuthStats(): Promise<any> {
    try {
      const response = await this.client.get(
        '/api/v1/oauth-v2/session/stats'
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as any;
      throw new Error(
        axiosErr.response?.data?.detail || 'Failed to fetch OAuth stats'
      );
    }
  }
}

const repoService = new RepositoryService();
export default repoService;
